import { WebphoneCallEvent } from '@modules/SoftPhoneModule/types';
import { phoneState } from '@modules/SoftPhoneModule/state';
import TasksModule from '@modules/TasksModule';
import { TaskState } from '@modules/TasksModule/types';
import DiagnosticsModule, {
  MetricNames,
  MetricTags,
} from '@modules/DiagnosticsModule';
import isRingAllQueue from '@lib/utils/isRingAllQueue';
import { InboxContext } from '../types';
import InboxModule from '..';
import { sipEventsForFailedTasksEnabled } from '../lib/sipEventsForFailedTasks';
import {
  setCallToAbandoned,
  setCallToTimedOut,
} from '../callHandlers/callStateHandler';
import { setOutboundCallToRejected } from '../callHandlers/outboundCalls';

/**
 * Low-cardinality `reason` tag values for the failed-task counters — the branch
 * this handler took for a given SIP failure.
 */
const Reason = {
  Outbound: 'outbound',
  NonQueue: 'nonqueue',
  Hangup: 'hangup',
  SimRing: 'simring',
  Timeout: 'timeout',
  Reject: 'reject',
  Unknown: 'unknown',
  NoTask: 'no_task',
  AlreadyHandled: 'already_handled',
} as const;
type ReasonValue = (typeof Reason)[keyof typeof Reason];

/**
 * Low-cardinality `outcome` tag values — whether this handler changed task state.
 * `transitioned` vs `skipped` is the phantom-prevention signal: how often the SIP
 * event did the work the (delayed/dropped) RTCS event would have.
 */
const Outcome = {
  Transitioned: 'transitioned', // a setCallTo* was invoked
  Noop: 'noop', // task was actionable but we deliberately did nothing
  Skipped: 'skipped', // no task, or RTCS/accept already handled it
} as const;
type OutcomeValue = (typeof Outcome)[keyof typeof Outcome];

/** The classification + outcome a single failed-task event resolved to. */
type Handled = { reason: ReasonValue; outcome: OutcomeValue };

/** Emit the per-event handled counter tagged with its reason + outcome. */
function trackHandled({ reason, outcome }: Handled) {
  DiagnosticsModule.incrementCounter(
    MetricNames.WEBPHONE_FAILED_TASK_HANDLED_TOTAL,
    {
      [MetricTags.REASON]: reason,
      [MetricTags.OUTCOME]: outcome,
    },
  );
}

/**
 * How a webphone `phone.call.failed-general` SIP event maps to a task transition
 * for an inbound *queue* task (one that already has an RTCS session).
 *
 * - `timeout`     — ring timeout / no answer → setCallToTimedOut
 * - `cancel`      — leg cancelled before answer (caller hangup, rollover, or
 *                   sim-ring answered elsewhere) → setCallToAbandoned, EXCEPT
 *                   ring-all queues which are left to RTCS
 * - `agentReject` — agent rejected the offer locally → no-op (handleTaskReject
 *                   already owns this case)
 * - `unknown`     — reason code not covered by the mapping → no transition
 */
type QueueFailureKind = 'timeout' | 'cancel' | 'agentReject' | 'unknown';

/**
 * `originator` for a failure the local endpoint caused (e.g. the agent rejecting
 * the offer), as opposed to `'remote'` for caller/network-side events. A local
 * failure carries no `sipResponse` at all.
 */
const ORIGINATOR_LOCAL = 'local';

/** `sipResponse.source` for a Q.850 cause code (e.g. 19 NO_ANSWER). */
const REASON_SOURCE_Q850 = 'Q.850';

/**
 * Q.850 19 NO_ANSWER — ring timeout / no answer. Arrives as a Q.850-source
 * failure, NOT a SIP one, which is how it is told apart from a 487 cancel.
 */
const Q850_NO_ANSWER = 19;

/**
 * SIP 487 (ORIGINATOR_CANCEL) — the call leg was cancelled before it was
 * answered. Caller hangup, queue rollover, and sim-ring-answered-elsewhere all
 * emit an identical SIP-487 payload (confirmed via captured events), so they are
 * indistinguishable here; sim-ring is separated downstream via `isRingAllQueue`,
 * exactly as the RTCS path does today.
 */
const SIP_REQUEST_TERMINATED = 487;

/**
 * Classify a `phone.call.failed-general` event for an inbound queue task.
 *
 * Mapping confirmed against captured webphone payloads (CCPE-425):
 * - agent reject:        `originator: 'local'`, `cause: 'Rejected'`, no sipResponse
 * - caller hangup / sim-ring / rollover: `originator: 'remote'`,
 *                        `sipResponse: { source: 'SIP', statusCode: 487 }`
 * - timeout / no answer: `sipResponse: { source: 'Q.850', statusCode: 19 }`
 *
 * All three rows are confirmed against real captured payloads. We classify off
 * `source` + `statusCode` (NOT `cause`, which is `'Canceled'` for both the
 * timeout and the 487 cancel). Order matters: agent-reject (local) is checked
 * before the remote-side cases.
 */
function classifyQueueFailure(event: WebphoneCallEvent): QueueFailureKind {
  if (event.originator === ORIGINATOR_LOCAL) {
    return 'agentReject';
  }

  const sip = event.sipResponse;

  if (sip?.source === REASON_SOURCE_Q850 && sip.statusCode === Q850_NO_ANSWER) {
    return 'timeout';
  }

  if (sip?.statusCode === SIP_REQUEST_TERMINATED) {
    return 'cancel';
  }

  return 'unknown';
}

/**
 * Resolve the task this SIP event belongs to.
 *
 * Unlike `handleCallMissed`, `WebphoneCallEvent` carries no `pcsConversationSid`,
 * so we first map the webphone `callId` to the PCS conversation sid via the
 * SoftPhone call attributes (the inbound-queue path), then fall back to a lookup
 * by callId, and finally by the event's `remoteCallId` for the outbound path.
 */
function resolveTask(event: WebphoneCallEvent) {
  const pcsConversationSid =
    phoneState.callAttributes[event.callId]?.pcsConversationSid;

  return (
    (pcsConversationSid ? TasksModule.getTask(pcsConversationSid) : null) ??
    TasksModule.getTaskByCallId(event.callId) ??
    (event.remoteCallId
      ? TasksModule.findOutboundCallTaskByRemoteCallId(event.remoteCallId)
      : null)
  );
}

/**
 * Decide what to do with a SIP failure for an inbound queue task (has session)
 * and perform the transition. Returns the {reason, outcome} for telemetry.
 *
 * - agentReject → no-op (owned by handleTaskReject; the SIP event fires for it too)
 * - timeout     → setCallToTimedOut (self-guards ring-all, so safe to call always)
 * - cancel      → setCallToAbandoned, EXCEPT ring-all (sim-ring) which is left to
 *                 RTCS — setCallToAbandoned does NOT self-guard ring-all
 * - unknown     → no transition (avoid a wrong dismiss); logged for visibility
 */
function handleQueueFailure(
  context: InboxContext,
  task: NonNullable<ReturnType<typeof resolveTask>>,
  event: WebphoneCallEvent,
): Handled {
  switch (classifyQueueFailure(event)) {
    case 'agentReject':
      return { reason: Reason.Reject, outcome: Outcome.Noop };

    case 'timeout':
      setCallToTimedOut({
        context,
        sessionSid: task.id,
        eventType: 'call-failed',
      });
      return { reason: Reason.Timeout, outcome: Outcome.Transitioned };

    case 'cancel':
      if (!isRingAllQueue(task.queueId ?? '')) {
        setCallToAbandoned({
          context,
          taskId: task.id,
          eventType: 'call-failed',
        });
        return { reason: Reason.Hangup, outcome: Outcome.Transitioned };
      }
      // Ring-all: sim-ring dismissal is left to RTCS.
      return { reason: Reason.SimRing, outcome: Outcome.Noop };

    case 'unknown':
    default:
      context.logger.info(
        'handleCallFailedGeneral: unmapped SIP failure, no transition',
        {
          callId: event.callId,
          originator: event.originator,
          cause: event.cause,
          statusCode: event.sipResponse?.statusCode,
        },
      );
      return { reason: Reason.Unknown, outcome: Outcome.Noop };
  }
}

/**
 * Resolve the failed task and perform the right transition, returning the
 * {reason, outcome} for telemetry. Branches:
 * - no task / non-Offered → skipped (non-Offered = RTCS / accept already handled
 *   it; this is the "RTCS got there first" half of the prevention signal)
 * - outbound / inbound-non-queue → mirror handleCallMissed (this handler owns
 *   them when the flag is ON)
 * - inbound queue (has session) → SIP-reason-code path (handleQueueFailure)
 */
function dispatchFailedTask(
  context: InboxContext,
  event: WebphoneCallEvent,
): Handled {
  const task = resolveTask(event);

  if (!task) {
    return { reason: Reason.NoTask, outcome: Outcome.Skipped };
  }

  if (task.state !== TaskState.Offered) {
    return { reason: Reason.AlreadyHandled, outcome: Outcome.Skipped };
  }

  if (task.isOutbound) {
    setOutboundCallToRejected(context, task.id);
    return { reason: Reason.Outbound, outcome: Outcome.Transitioned };
  }

  if (!InboxModule.hasSession(task)) {
    setCallToAbandoned({
      context,
      taskId: task.id,
      eventType: 'call-failed',
    });
    return { reason: Reason.NonQueue, outcome: Outcome.Transitioned };
  }

  return handleQueueFailure(context, task, event);
}

/**
 * Subscriber for the webphone `phone.call.failed-general` SIP event (CCPE-425).
 *
 * Drives inbound *queue* task transitions from the SIP call-failed reason code
 * instead of waiting on RTCS session events, which can be delayed or dropped and
 * leave a "phantom" task card stuck in the Offered state (CCPE-298). When the
 * `feature-use-sip-events-for-failed-tasks` flag is ON this handler also owns the
 * outbound and inbound-non-queue paths that `handleCallMissed` handles today, so
 * the failed-task logic is consolidated onto one event source.
 *
 * Co-existence with RTCS: every transition is guarded by `task.state === Offered`.
 * Whichever of RTCS / SIP fires *second* finds the task already transitioned and
 * no-ops, so there is never a double transition. Flag OFF → this handler returns
 * immediately and behavior is unchanged.
 *
 * Telemetry: a `received_total` counter (volume of SIP failures seen while the
 * feature is on) plus exactly one `handled_total{reason, outcome}` counter per
 * event. The `outcome` dimension (transitioned vs skipped) is the phantom-
 * prevention signal — how often SIP did the work a delayed/dropped RTCS event
 * would have. Routing all branches through dispatchFailedTask keeps the
 * one-counter-per-event guarantee structural rather than convention-enforced.
 */
function handleCallFailedGeneral(
  context: InboxContext,
  event: WebphoneCallEvent,
) {
  // Flag gate: the entire SIP-driven path is opt-in. When OFF, RTCS and
  // handleCallMissed keep their current ownership and this handler does nothing
  // (no telemetry either — we only measure where the feature is active).
  if (!sipEventsForFailedTasksEnabled()) {
    return;
  }

  DiagnosticsModule.incrementCounter(
    MetricNames.WEBPHONE_FAILED_TASK_RECEIVED_TOTAL,
  );

  trackHandled(dispatchFailedTask(context, event));
}

export default handleCallFailedGeneral;
